// ── PRICING PAGE (fp-nofodesk-9) — extracted from App.js ──
import { useState, useEffect } from "react";

import { FONT_H, FONT_UI, useT } from "../theme";
import { useSubscription } from "../context/SubscriptionContext";
import { API, apiFetch } from "../utils/api";

// ── PRICING VIEW ──────────────────────────────────────────────
function PricingView({ authUser }) {
  const T   = useT();
  const sub = useSubscription();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [toast,       setToast]       = useState(null);
  const [currency,    setCurrency]    = useState("USD");
  const [rate,        setRate]        = useState(1);
  const [loadingFx,   setLoadingFx]   = useState(true);
  const [billing,     setBilling]     = useState("monthly"); // "monthly" | "annual"

  const showToast = (msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 4000);
  };

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [geo, fx] = await Promise.all([
          fetch("https://ipapi.co/json/").then(r => r.json()),
          fetch("https://open.er-api.com/v6/latest/USD").then(r => r.json()),
        ]);
        if (!live) return;
        const cur = geo.currency || "USD";
        setCurrency(cur);
        setRate(fx.rates?.[cur] || 1);
      } catch { /* stay USD */ }
      if (live) setLoadingFx(false);
    })();
    return () => { live = false; };
  }, []);

  // Fixed NGN prices — stable regardless of exchange rate fluctuation
  const NGN_PRICES = {
    base20: 25000, base20a: 240000,
  };

  const isAnnual = billing === "annual";

  const fmt = (usd, ngnFixed = null) => {
    const amount = (currency === "NGN" && ngnFixed !== null) ? ngnFixed : Math.round(usd * rate);
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency", currency,
        minimumFractionDigits: 0, maximumFractionDigits: 0,
      }).format(amount);
    } catch { return `${amount}`; }
  };

  // Monthly effective rates (annual = /12 of annual total).
  // Individual pricing is self-serve; Team & Enterprise are quoted per
  // organisation (NGOs typically need contracts, invoices, and procurement
  // documentation) — those tiers route to sales instead of checkout.
  const proMonthlyUSD  = isAnnual ? 39 : 49;
  const proMonthlyNGN  = isAnnual ? NGN_PRICES.base20a / 12 : NGN_PRICES.base20;

  const currentPlan = sub.plan;
  const isFree = currentPlan === "free";
  const isPro  = currentPlan === "pro";
  const isOrg  = ["org", "beta"].includes(currentPlan);

  const DIRECT_LINKS = {
    pro:        "https://flutterwave.com/pay/vdvhoxftigdv",
    pro_annual: "https://flutterwave.com/pay/c6csilpzj9ey",
  };

  const SALES_MAILTO = "mailto:info@nofodesk.com?subject=" + encodeURIComponent("NofoDesk Team / Enterprise plan");

  const handleUpgrade = async (basePlan) => {
    if (!authUser) { showToast("Please log in first.", false); return; }
    const plan = isAnnual ? `${basePlan}_annual` : basePlan;
    if (currency === "USD" && DIRECT_LINKS[plan]) {
      window.location.href = DIRECT_LINKS[plan];
      return;
    }
    setLoadingPlan(basePlan);
    try {
      const res = await apiFetch(`${API}/payments/flutterwave/initiate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan, currency }),
      });
      const data = await res.json();
      if (!res.ok) { showToast(data.error || "Payment init failed.", false); return; }
      window.location.href = data.payment_link;
    } catch { showToast("Could not start payment. Try again.", false); }
    finally   { setLoadingPlan(null); }
  };

  const CHECK = <span style={{ color: "#22D4A8", fontSize: 13, flexShrink: 0, marginTop: 1 }}>✓</span>;
  const CROSS = <span style={{ color: T.textFaint, fontSize: 12, flexShrink: 0, marginTop: 1, opacity: 0.4 }}>✕</span>;
  const feat = (icon, text, muted = false) => (
    <div key={text} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12.5, color: muted ? T.textFaint : T.textBody, marginBottom: 8, opacity: muted ? 0.5 : 1, lineHeight: 1.4 }}>
      {icon}<span>{text}</span>
    </div>
  );

  return (
    <div style={{ padding: "32px 28px", maxWidth: 1040, margin: "0 auto", fontFamily: FONT_UI }}>

      {toast && (
        <div style={{ position: "fixed", top: 20, right: 24, padding: "12px 20px", borderRadius: 8, background: toast.ok ? "#10B981" : "#EF4444", color: "#fff", fontSize: 13, fontWeight: 600, zIndex: 1000, boxShadow: "0 4px 16px rgba(0,0,0,0.3)" }}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, flexWrap: "wrap", gap: 10 }}>
        <div>
          <div style={{ fontSize: 26, fontFamily: FONT_H, color: T.text, fontWeight: 400, marginBottom: 4 }}>Plans & Billing</div>
          <div style={{ fontSize: 13, color: T.textMuted }}>
            Current plan:{" "}
            <strong style={{ color: T.gold, textTransform: "capitalize" }}>
              {currentPlan === "beta" ? "Beta (Full Access)" : currentPlan}
            </strong>
            {sub.expiresAt && (
              <span style={{ marginLeft: 10, color: T.textFaint, fontSize: 11 }}>
                Renews {new Date(sub.expiresAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
              </span>
            )}
          </div>
        </div>
        {!loadingFx && (
          <div style={{ fontSize: 11, color: T.textMuted, background: T.bgElevated, border: `1px solid ${T.border}`, borderRadius: 20, padding: "4px 12px", whiteSpace: "nowrap" }}>
            Prices in <strong style={{ color: T.text }}>{currency}</strong>
          </div>
        )}
      </div>

      {/* Billing toggle */}
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 28 }}>
        <div style={{ display: "inline-flex", background: T.bgSurface, border: `1px solid ${T.border}`, borderRadius: 10, padding: 3, gap: 2 }}>
          {["monthly", "annual"].map(b => (
            <button
              key={b}
              onClick={() => setBilling(b)}
              style={{ padding: "8px 22px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, fontFamily: FONT_UI, transition: "all 0.15s",
                background: billing === b ? T.gold : "transparent",
                color: billing === b ? "#fff" : T.textMuted,
              }}
            >
              {b === "monthly" ? "Monthly" : "Annual"}
              {b === "annual" && <span style={{ marginLeft: 6, fontSize: 10, background: "rgba(34,212,168,0.2)", color: "#22D4A8", padding: "1px 6px", borderRadius: 10, fontWeight: 700 }}>20% OFF</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 18, alignItems: "start" }}>

        {/* FREE */}
        <div style={{ borderRadius: 14, padding: "26px 22px", background: T.bgSurface, border: `${isFree ? 2 : 1}px solid ${isFree ? T.gold : T.border}`, display: "flex", flexDirection: "column", position: "relative" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.textMuted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 14 }}>Free</div>
          <div style={{ marginBottom: 6 }}>
            <span style={{ fontSize: 36, fontWeight: 800, color: T.text, fontFamily: FONT_H, letterSpacing: "-0.03em" }}>{fmt(0)}</span>
            <span style={{ fontSize: 13, color: T.textMuted, marginLeft: 6 }}>forever</span>
          </div>
          <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 20 }}>One person · no credit card needed</div>
          <div style={{ flex: 1, marginBottom: 20 }}>
            {feat(CHECK, "Up to 10 grants & 25 records")}
            {feat(CHECK, "5 AI extractions / month")}
            {feat(CHECK, "Grant tracker & pipeline")}
            {feat(CHECK, "Calendar & deadline alerts")}
            {feat(CHECK, "Basic dashboard & reports")}
            {feat(CHECK, "Grant Discovery (browse only)")}
            {feat(CROSS, "AI proposal writing", true)}
            {feat(CROSS, "Google Drive & OneDrive sync", true)}
            {feat(CROSS, "Export to PowerPoint", true)}
            {feat(CROSS, "Version history", true)}
            {feat(CROSS, "Recycle bin", true)}
            {feat(CROSS, "Priority support", true)}
          </div>
          <button disabled style={{ padding: "11px 0", borderRadius: 8, fontWeight: 700, fontSize: 13, background: "transparent", color: T.textMuted, border: `1px solid ${T.border}`, cursor: "default", fontFamily: FONT_UI }}>
            {isFree ? "✓ Current Plan" : "Free Plan"}
          </button>
        </div>

        {/* PRO */}
        <div style={{ borderRadius: 14, padding: "26px 22px", background: T.bgElevated, border: `${isPro ? 2 : 1}px solid ${isPro ? T.gold : T.gold + "55"}`, display: "flex", flexDirection: "column", position: "relative", boxShadow: `0 4px 28px ${T.gold}14` }}>
          <div style={{ position: "absolute", top: -11, left: "50%", transform: "translateX(-50%)", background: T.gold, color: "#fff", fontSize: 10, fontWeight: 800, padding: "3px 14px", borderRadius: 20, letterSpacing: "0.08em", whiteSpace: "nowrap" }}>
            MOST POPULAR
          </div>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.gold, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 14 }}>Individual Pro</div>
          <div style={{ marginBottom: 2 }}>
            <span style={{ fontSize: 36, fontWeight: 800, color: T.gold, fontFamily: FONT_H, letterSpacing: "-0.03em" }}>
              {loadingFx ? "…" : fmt(proMonthlyUSD, proMonthlyNGN)}
            </span>
            <span style={{ fontSize: 13, color: T.textMuted, marginLeft: 6 }}>/ mo</span>
          </div>
          {isAnnual && <div style={{ fontSize: 11, color: "#22D4A8", marginBottom: 4 }}>Billed {loadingFx ? "…" : fmt(proMonthlyUSD * 12, NGN_PRICES.base20a)}/year</div>}
          <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 20 }}>One person · cancel anytime</div>
          <div style={{ flex: 1, marginBottom: 20 }}>
            {feat(CHECK, "Unlimited grants & records")}
            {feat(CHECK, "50 AI extractions / month")}
            {feat(CHECK, "AI proposal writing")}
            {feat(CHECK, "Google Drive integration")}
            {feat(CHECK, "OneDrive integration")}
            {feat(CHECK, "Auto folder organisation")}
            {feat(CHECK, "Profile-matched grant discovery")}
            {feat(CHECK, "Export to PowerPoint")}
            {feat(CHECK, "Version history (30 days)")}
            {feat(CHECK, "Recycle bin (90 days)")}
            {feat(CHECK, "Email deadline alerts (90, 30, 7, 3, 1 day)")}
            {feat(CHECK, "Priority email support")}
            {feat(CROSS, "Team workspaces", true)}
            {feat(CROSS, "Multi-user access", true)}
          </div>
          <button
            disabled={isPro || loadingPlan !== null}
            onClick={() => handleUpgrade("pro")}
            style={{ padding: "12px 0", borderRadius: 8, fontWeight: 700, fontSize: 13, background: isPro ? T.bgSurface : T.gold, color: isPro ? T.textMuted : "#fff", border: isPro ? `1px solid ${T.border}` : "none", cursor: isPro ? "default" : "pointer", opacity: loadingPlan && loadingPlan !== "pro" ? 0.4 : 1, fontFamily: FONT_UI, transition: "opacity 0.15s" }}
          >
            {loadingPlan === "pro" ? "Redirecting…" : isPro ? "✓ Current Plan" : "Upgrade to Pro →"}
          </button>
        </div>

        {/* TEAM — quoted per organisation, sales-led */}
        <div style={{ borderRadius: 14, padding: "26px 22px", background: T.bgSurface, border: `${isOrg ? 2 : 1}px solid ${isOrg ? T.gold : T.border}`, display: "flex", flexDirection: "column", position: "relative" }}>
          {isOrg && (
            <div style={{ position: "absolute", top: -11, left: "50%", transform: "translateX(-50%)", background: T.gold, color: "#fff", fontSize: 10, fontWeight: 800, padding: "3px 14px", borderRadius: 20, letterSpacing: "0.08em", whiteSpace: "nowrap" }}>
              YOUR PLAN
            </div>
          )}
          <div style={{ fontSize: 11, fontWeight: 700, color: T.textMuted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 14 }}>Team</div>
          <div style={{ marginBottom: 2 }}>
            <span style={{ fontSize: 36, fontWeight: 800, color: T.text, fontFamily: FONT_H, letterSpacing: "-0.03em" }}>Custom</span>
          </div>
          <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 20 }}>
            Quoted per organisation · from 5 seats
          </div>
          <div style={{ flex: 1, marginBottom: 20 }}>
            {feat(CHECK, "Everything in Pro")}
            {feat(CHECK, "Seats sized to your team")}
            {feat(CHECK, "Shared grant workspaces")}
            {feat(CHECK, "Role-based access (Editor / Viewer)")}
            {feat(CHECK, "Unlimited AI extractions")}
            {feat(CHECK, "Collaborative proposal writing")}
            {feat(CHECK, "Shared Drive folders per grant")}
            {feat(CHECK, "Email alerts to Point of Contact")}
            {feat(CHECK, "Audit trail & activity log")}
            {feat(CHECK, "Contract & invoice billing")}
            {feat(CHECK, "Dedicated support & onboarding")}
          </div>
          <a
            href={SALES_MAILTO}
            style={{ display: "block", textAlign: "center", padding: "12px 0", borderRadius: 8, fontWeight: 700, fontSize: 13, background: isOrg ? T.bgSurface : T.text, color: isOrg ? T.textMuted : T.bgApp, border: isOrg ? `1px solid ${T.border}` : "none", textDecoration: "none", fontFamily: FONT_UI }}
          >
            {isOrg ? "✓ Current Plan" : "Contact Sales →"}
          </a>
        </div>

        {/* ENTERPRISE — sales-led */}
        <div style={{ borderRadius: 14, padding: "26px 22px", background: T.bgSurface, border: `1px solid ${T.border}`, display: "flex", flexDirection: "column", position: "relative" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.textMuted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 14 }}>Enterprise</div>
          <div style={{ marginBottom: 2 }}>
            <span style={{ fontSize: 36, fontWeight: 800, color: T.text, fontFamily: FONT_H, letterSpacing: "-0.03em" }}>Custom</span>
          </div>
          <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 20 }}>
            For NGOs & institutions with procurement requirements
          </div>
          <div style={{ flex: 1, marginBottom: 20 }}>
            {feat(CHECK, "Everything in Team")}
            {feat(CHECK, "Custom seat count across departments")}
            {feat(CHECK, "Annual contracts / MOUs")}
            {feat(CHECK, "Invoice & bank-transfer billing")}
            {feat(CHECK, "Vendor & procurement documentation")}
            {feat(CHECK, "Dedicated onboarding for your team")}
            {feat(CHECK, "Priority support with named contact")}
          </div>
          <a
            href={SALES_MAILTO}
            style={{ display: "block", textAlign: "center", padding: "12px 0", borderRadius: 8, fontWeight: 700, fontSize: 13, background: "transparent", color: T.text, border: `1px solid ${T.text}`, textDecoration: "none", fontFamily: FONT_UI }}
          >
            Contact Sales →
          </a>
        </div>

      </div>

      {/* Footer */}
      <div style={{ marginTop: 32, padding: "20px 24px", borderRadius: 12, background: T.bgElevated, border: `1px solid ${T.border}`, fontSize: 12, color: T.textMuted, lineHeight: 1.8 }}>
        <div style={{ display: "flex", gap: 40, flexWrap: "wrap" }}>
          <div>
            <strong style={{ color: T.text, fontSize: 13 }}>Payment & Billing</strong><br />
            Processed securely via Flutterwave. Monthly plans renew each month; annual plans are charged once per year.<br />
            To cancel, email{" "}
            <a href="mailto:info@nofodesk.com" style={{ color: T.gold }}>info@nofodesk.com</a>{" "}
            before your renewal date.
          </div>
          <div>
            <strong style={{ color: T.text, fontSize: 13 }}>Currencies & Cards</strong><br />
            Prices shown in <strong style={{ color: T.text }}>{currency}</strong> at live exchange rate.<br />
            NGN, USD, EUR, GBP and all major cards accepted.
          </div>
          <div>
            <strong style={{ color: T.text, fontSize: 13 }}>Legal</strong><br />
            <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{ color: T.gold }}>Privacy Policy</a><br />
            &copy; {new Date().getFullYear()} NofoDesk. All rights reserved.<br />
            A product of FOKT Consulting LLC.
          </div>
        </div>
      </div>

    </div>
  );
}

export default PricingView;
